import React, { useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { useDroppable } from "@dnd-kit/core";
import { DeliveryColumnHeader } from "./DeliveryColumnHeader";
import { VirtualizedColumnItems } from "./VirtualizedColumnItems";
import { getStageTotalLoads } from "./kanbanDisplay";
import { columnDroppableId, stageIdsEqual } from "app/utils/kanbanDnD";

const COLLAPSED_COLUMN_WIDTH = 88;
const EMPTY_SELECTED_IDS: number[] = [];

const getExpandedDropTargetSx = (isDropHighlighted: boolean) =>
  isDropHighlighted
    ? {
        bgcolor: "#fff",
        borderRadius: "14px",
        boxShadow:
          "0 16px 40px rgba(15, 23, 42, 0.22), 0 6px 16px rgba(15, 23, 42, 0.14)",
        transform: "translateY(-4px)",
        zIndex: 4,
      }
    : {
        bgcolor: "transparent",
        borderRadius: "14px",
        boxShadow: "none",
        transform: "none",
        zIndex: 1,
      };

const getCollapsedDropTargetSx = (isDropHighlighted: boolean) =>
  isDropHighlighted
    ? {
        bgcolor: "#fff",
        border: "1px solid #e3e6ee",
        boxShadow:
          "0 16px 40px rgba(15, 23, 42, 0.22), 0 6px 16px rgba(15, 23, 42, 0.14)",
        transform: "translateY(-4px) scale(1.02)",
        zIndex: 4,
      }
    : {
        bgcolor: "#fff",
        border: "1px solid #e3e6ee",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        transform: "none",
        zIndex: 1,
      };

export type BoardStage = {
  id: number | string;
  title: string;
  color?: string;
  items: any[];
};

type DeliveryBoardColumnProps = {
  stage: BoardStage;
  collapsed: boolean;
  userTimezone: string;
  selectedIdsInStage: number[];
  onToggleCollapse: (stageId: number | string) => void;
  onToggleItem: (itemId: number, checked: boolean) => void;
  onToggleColumn: (itemIds: number[], checked: boolean) => void;
  dragDisabled?: boolean;
  dropTargetStageId?: number | string | null;
};

function DeliveryBoardColumnComponent({
  stage,
  collapsed,
  userTimezone,
  selectedIdsInStage,
  onToggleCollapse,
  onToggleItem,
  onToggleColumn,
  dragDisabled = false,
  dropTargetStageId = null,
}: DeliveryBoardColumnProps) {
  const items = stage.items ?? [];
  const itemCount = items.length;
  const totalLoads = getStageTotalLoads(stage);
  const selectedSet = useMemo(
    () => new Set(selectedIdsInStage),
    [selectedIdsInStage]
  );

  const isSelectAll =
    itemCount > 0 && selectedIdsInStage.length === itemCount;

  const itemIds = useMemo(
    () => items.map((item) => item.id as number),
    [items]
  );

  const { setNodeRef, isOver } = useDroppable({
    id: columnDroppableId(stage.id),
    data: {
      type: "column",
      stageId: stage.id,
    },
    disabled: dragDisabled,
  });

  const isDropHighlighted =
    isOver ||
    (dropTargetStageId != null && stageIdsEqual(stage.id, dropTargetStageId));

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onToggleColumn(itemIds, event.target.checked);
    },
    [itemIds, onToggleColumn]
  );

  if (collapsed) {
    return (
      <Box
        ref={setNodeRef}
        sx={{
          flex: `0 0 ${COLLAPSED_COLUMN_WIDTH}px`,
          width: COLLAPSED_COLUMN_WIDTH,
          minWidth: COLLAPSED_COLUMN_WIDTH,
          maxWidth: COLLAPSED_COLUMN_WIDTH,
          height: "100%",
          flexShrink: 0,
        }}
      >
        <Box
          onClick={() => onToggleCollapse(stage.id)}
          title={`Expand ${stage.title}`}
          role="button"
          aria-label={`Expand ${stage.title}`}
          sx={{
            position: "relative",
            height: "100%",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            overflow: "hidden",
            transition:
              "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, transform 0.2s ease",
            ...getCollapsedDropTargetSx(isDropHighlighted),
            ...(!isDropHighlighted
              ? {
                  "&:hover": {
                    borderColor: "#7ec597",
                    boxShadow: "0 6px 18px rgba(126, 197, 151, 0.22)",
                    "& .collapsed-expand-btn": {
                      bgcolor: "#7ec597",
                      color: "#fff",
                    },
                  },
                }
              : {}),
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              bgcolor: stage.color || "#808080",
              borderTopLeftRadius: "10px",
              borderBottomLeftRadius: "10px",
            }}
          />
          <Box
            className="collapsed-expand-btn"
            sx={{
              mt: 1.5,
              mb: 1,
              width: 40,
              height: 40,
              minWidth: 40,
              minHeight: 40,
              borderRadius: "10px",
              bgcolor: "#eef7f1",
              color: "#5fa67a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              zIndex: 1,
            }}
          >
            <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1,
              pb: 2,
              minHeight: 0,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: "#625a57",
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                whiteSpace: "nowrap",
                letterSpacing: "0.6px",
                fontSize: "0.95rem",
                lineHeight: 1.2,
                maxHeight: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stage.title}
            </Typography>
          </Box>
          <Box
            sx={{
              mb: 1.5,
              px: 1,
              py: 0.5,
              borderRadius: "999px",
              bgcolor: "#f3f4f7",
              color: "#737372",
              fontSize: "0.82rem",
              fontWeight: 700,
              lineHeight: 1,
              zIndex: 1,
            }}
          >
            {itemCount}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: "1 1 300px",
        width: "auto",
        minWidth: 300,
        maxWidth: 420,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: "relative",
        p: 0.5,
        transition: "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
        ...getExpandedDropTargetSx(isDropHighlighted),
      }}
    >
      <DeliveryColumnHeader
        title={stage.title}
        itemCount={itemCount}
        totalLoads={totalLoads}
        isSelectAll={isSelectAll}
        onSelectAllChange={handleSelectAll}
        showCollapse={itemCount === 0}
        onCollapse={() => onToggleCollapse(stage.id)}
        disableSelectAll={itemCount === 0}
        highlighted={isDropHighlighted}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: itemCount > 30 ? "hidden" : "auto",
          overflowX: "hidden",
          pr: itemCount > 30 ? 0 : 0.5,
          pb: 1,
          scrollbarWidth: "thin",
          scrollbarColor: "#cbd5e1 transparent",
          borderRadius: "0 0 10px 10px",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cbd5e1",
            borderRadius: "999px",
          },
        }}
      >
        {itemCount !== 0 ? (
          <VirtualizedColumnItems
            items={items}
            stageId={stage.id}
            stageColor={stage.color}
            userTimezone={userTimezone}
            selectedSet={selectedSet}
            onToggleItem={onToggleItem}
            dragDisabled={dragDisabled}
          />
        ) : (
          <Box
            sx={{
              py: 4,
              px: 2,
              mx: 1,
              border: "1px dashed #dbe2ea",
              borderRadius: "12px",
              bgcolor: isDropHighlighted ? "#f9fafb" : "#fff",
              textAlign: "center",
              boxShadow: isDropHighlighted
                ? "inset 0 2px 8px rgba(15, 23, 42, 0.06)"
                : "none",
              transition: "background-color 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: isDropHighlighted ? "#6b7280" : "#9ca3af",
                fontWeight: isDropHighlighted ? 600 : 400,
              }}
            >
              {isDropHighlighted ? "Release to move here" : "No orders for this area"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function stageItemsEqual(a: BoardStage, b: BoardStage): boolean {
  if (!stageIdsEqual(a.id, b.id)) {
    return false;
  }
  const aItems = a.items ?? [];
  const bItems = b.items ?? [];
  if (aItems.length !== bItems.length) {
    return false;
  }
  for (let index = 0; index < aItems.length; index += 1) {
    if (String(aItems[index].id) !== String(bItems[index].id)) {
      return false;
    }
  }
  return true;
}

function areSelectedIdsEqual(a: number[], b: number[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return false;
    }
  }
  return true;
}

export const DeliveryBoardColumn = React.memo(
  DeliveryBoardColumnComponent,
  (prev, next) =>
    stageItemsEqual(prev.stage, next.stage) &&
    prev.collapsed === next.collapsed &&
    prev.userTimezone === next.userTimezone &&
    prev.dragDisabled === next.dragDisabled &&
    stageIdsEqual(prev.dropTargetStageId ?? null, next.dropTargetStageId ?? null) &&
    prev.onToggleItem === next.onToggleItem &&
    prev.onToggleColumn === next.onToggleColumn &&
    prev.onToggleCollapse === next.onToggleCollapse &&
    areSelectedIdsEqual(prev.selectedIdsInStage, next.selectedIdsInStage)
);

export { EMPTY_SELECTED_IDS };
